import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from './GoogleSignInButton';
import SeoHead from './SeoHead';
import { pageMeta } from '../seoUtils';
import { trackEvent } from '../analyticsLoader';
import { PasswordInput } from './index';

export default function SignUpPage({ onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { register, loading, error, clearError, user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState('');
  // A fresh password registration navigates to /check-your-email itself
  // (handleSubmit, below) rather than the redirect target, so this guard -
  // meant for an already-logged-in visitor landing on /signup - needs to
  // skip firing in that case, or it would redirect them there immediately.
  const skipAutoRedirect = useRef(false);
  const stateFrom = location.state?.from;
  const redirectPath = typeof stateFrom === 'string' && stateFrom.startsWith('/') ? stateFrom : '/';

  // Redirect if already logged in
  useEffect(() => {
    if (user && !skipAutoRedirect.current) {
      onNavigate(redirectPath);
    }
  }, [user, onNavigate, redirectPath]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const validate = () => {
    if (!name.trim()) {
      return 'Please enter your full name.';
    }
    if (!email.trim()) {
      return 'Please enter your email address.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Please enter a valid email address.';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setFieldError(validationError);
      return;
    }
    setFieldError('');

    const referralCode = new URLSearchParams(window.location.search).get('ref') || undefined;
    const result = await register({
      name: name.trim(),
      email: email.trim(),
      password,
      role: 'buyer',
      referralCode,
    });
    if (result.ok) {
      trackEvent('signup_completed', { method: 'password' });
      skipAutoRedirect.current = true;
      onNavigate('/check-your-email');
    }
  };

  const handleGoogleSuccess = (data) => {
    // Token already stored by GoogleSignInButton
    trackEvent('signup_completed', { method: 'google' });
    onNavigate(redirectPath);
  };

  const handleGoogleError = (errorMsg) => {
    setFieldError(errorMsg);
  };

  const displayError = fieldError || error;

  const seo = pageMeta({
    title: 'Sign Up',
    description:
      'Create a free Naija Nest Properties account to list and browse properties across Nigeria.',
    path: '/signup',
    noIndex: true,
  });

  return (
    <main className="auth-page">
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        noIndex={seo.noIndex}
      />
      <div className="auth-card">
        <div className="auth-card__header">
          <h1 className="auth-card__title">Create your account</h1>
          <p className="auth-card__subtitle">
            Join NaijaNest — Nigeria&rsquo;s property marketplace
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {displayError && (
            <div className="auth-form__error" role="alert">
              {displayError}
            </div>
          )}

          <div className="auth-form__field">
            <label htmlFor="signup-name" className="auth-form__label">
              Full name
            </label>
            <input
              id="signup-name"
              type="text"
              className="auth-form__input"
              placeholder="Emeka Okafor"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="signup-email" className="auth-form__label">
              Email address
            </label>
            <input
              id="signup-email"
              type="email"
              className="auth-form__input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-form__field">
            <label htmlFor="signup-password" className="auth-form__label">
              Password
            </label>
            <PasswordInput
              id="signup-password"
              className="auth-form__input"
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>

          <button type="submit" className="auth-form__submit" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="auth-card__divider">
          <span>Or</span>
        </div>

        <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={handleGoogleError} />

        <p className="auth-card__footer">
          Already have an account?{' '}
          <button
            type="button"
            className="auth-card__link"
            onClick={() => navigate('/login', { state: stateFrom ? { from: stateFrom } : undefined })}
          >
            Log in
          </button>
        </p>
      </div>
    </main>
  );
}
